import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, X, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
} from "./utils";

/* eslint-disable no-unused-vars */

export default function PersonAvailabilityMenu({ person, day, onSaved }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("vacation"); // "vacation" | "unavailable"
  const [start, setStart] = useState(day);
  const [end, setEnd] = useState(day);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset on close
  useEffect(() => {
    if (open) {
      setKind("vacation");
      setStart(day);
      setEnd(day);
      setNote("");
    }
  }, [open, day]);

  // Verifică dacă un string YYYY-MM-DD reprezintă o dată calendaristică validă
  const isValidIsoDate = (s) => {
    if (!s || typeof s !== "string") return false;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return false;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (mo < 1 || mo > 12) return false;
    const dt = new Date(y, mo - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
  };

  const submit = async () => {
    if (!start || !end) { toast.error("Setează datele"); return; }
    if (!isValidIsoDate(start) || !isValidIsoDate(end)) {
      toast.error("Data introdusă nu există (ex: 31.06 — iunie are doar 30 zile). Corectează data."); return;
    }
    if (end < start) { toast.error("Data sfârșit nu poate fi înainte de data început"); return; }
    if (kind === "unavailable" && !note.trim()) { toast.error("Motivul indisponibilității este obligatoriu"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/vacations", {
        person_id: person.id,
        person_name: person.name,
        start_date: start,
        end_date: end,
        note: note.trim() || null,
        kind,
      });
      const removed = res?.data?.schedule_entries_affected || 0;
      const baseMsg = kind === "vacation"
        ? `${person.name} este în concediu de la ${start} până la ${end}`
        : `${person.name} marcat indisponibil de la ${start} până la ${end}`;
      toast.success(baseMsg + (removed > 0 ? ` · ${removed} alocări scoase` : ""));
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSubmitting(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="px-2 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-r border-l border-zinc-200 flex items-center transition-colors"
          title="Trimite în concediu / marchează indisponibil"
          data-testid={`person-avail-menu-${person.id}`}
        >
          ⋯
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-3 space-y-2"
        side="left"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display font-bold text-sm">{person.name}</div>
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => setKind("vacation")}
            className={`text-xs py-2 rounded font-semibold border transition-colors ${kind === "vacation" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-zinc-300 text-zinc-700"}`}
            data-testid={`avail-kind-vacation-${person.id}`}
          >
            🏖 Concediu
          </button>
          <button
            type="button"
            onClick={() => setKind("unavailable")}
            className={`text-xs py-2 rounded font-semibold border transition-colors ${kind === "unavailable" ? "bg-amber-500 text-white border-amber-500" : "bg-white border-zinc-300 text-zinc-700"}`}
            data-testid={`avail-kind-unavailable-${person.id}`}
          >
            🚫 Indisponibil
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">De la</Label>
            <Input type="date" className="h-9 text-xs" value={start} onChange={(e) => setStart(e.target.value)} data-testid={`avail-start-${person.id}`}/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Până la</Label>
            <Input type="date" className="h-9 text-xs" value={end} onChange={(e) => setEnd(e.target.value)} data-testid={`avail-end-${person.id}`}/>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-zinc-500">
            {kind === "unavailable" ? "Motiv (obligatoriu)" : "Notă (opțional)"}
          </Label>
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="text-xs"
            placeholder={kind === "unavailable" ? "ex: programare medicală, training..." : "ex: concediu de odihnă"}
            data-testid={`avail-note-${person.id}`}
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Anulează</Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={submitting}
            className={kind === "vacation" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}
            data-testid={`avail-submit-${person.id}`}
          >
            {submitting ? "..." : "Salvează"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
